import { addNotification } from '~/cynqueModules/addNotification'
import { reloadPage } from '~/cynqueModules/reloadPage'

/**
 * Recover when a lazily-imported chunk 404s because the server was redeployed
 * under a live tab.
 *
 * WHY THIS EXISTS: Vite content-hashes every chunk and a deploy replaces the
 * whole `_nuxt/` directory, so a tab still running the previous build gets a 404
 * the moment it imports anything it had not already fetched. Vite fires
 * `vite:preloadError`, Nuxt re-emits it as `app:chunkError`, and the import
 * promise rejects. Honeybadger 133594517 is one of these: a tab on build
 * `06792f2f` asking for `/_nuxt/DisB2FUh.js` 52 minutes after `f2b8d632` went
 * out, reported as `UnhandledPromiseRejectionWarning: Failed to fetch
 * dynamically imported module`.
 *
 * WHY NUXT'S OWN PLUGIN IS NOT ENOUGH: `experimental.emitRouteChunkError`
 * defaults to `'automatic'`, which adds `nuxt:chunk-reload`. That plugin only
 * recovers through `router.onError`, so it covers a chunk error raised while
 * resolving a ROUTE component and nothing else. This app's other lazy imports
 * are not router-mediated and therefore had no recovery at all:
 *
 *   - services/entityInputFieldsService/getEntityInputFields.ts — the
 *     `import.meta.glob` loader behind every form/dialog's field spec. Its
 *     `await loadInputFields()` has no catch, so a stale chunk left an empty
 *     form on screen with no message.
 *   - components/ListPage.vue — the `*Card.vue` and top-button-bar select-value
 *     globs behind `defineAsyncComponent`.
 *   - cynqueModules/mixins/DragToReorderChildTablesMixin.ts — same idiom.
 *
 * (cynqueModules/store/piniaStoreRegistry.ts globs `eager`, so its stores are in
 * the entry chunk and cannot fail this way.)
 *
 * WHY A RELOAD, GIVEN plugins/05 DELIBERATELY DOES NOT RELOAD: 05 keeps a failed
 * click non-destructive because the user's typed input is still on screen and
 * still valid. Here it is the code itself that is missing — no retry, no
 * dialog and no amount of waiting can produce it, because the file is gone from
 * the server. The only recovery is to fetch the new build. Jerry's call,
 * 2026-08-17: notify first, then reload, so the reload is explained rather than
 * appearing as a spontaneous page flash.
 *
 * Page state survives regardless: the Pinia persistence plugin (plugins/00.store)
 * writes the 60 page-state stores to localStorage as they mutate, and the auth
 * token lives in sessionStorage. What is lost is anything typed into an open
 * dialog and not yet saved.
 *
 * This is a production-build path. In dev, `import()` is native and Vite does
 * not route it through the preload helper that dispatches `vite:preloadError`,
 * so this effectively never fires under `nuxi dev` or the Playwright suite.
 */

/** Long enough to read the notification, short enough not to feel stuck. */
const RELOAD_DELAY_MS = 3000

/**
 * Suppression window for the reload-loop guard. If a reload we triggered did not
 * fix things, the build on the server is broken rather than merely newer, and
 * reloading again would spin. Outlives `reloadNuxtApp`'s own 10s `nuxt:reload`
 * marker deliberately — one bounce is a deploy, two is a bad build.
 */
const RELOAD_GUARD_MS = 60_000

const RELOAD_GUARD_KEY = 'cynque:chunkErrorReloadAt'

/** Read the guard stamp. Private-mode sessionStorage throws; treat that as "no stamp". */
function reloadedRecently(): boolean {
  try {
    const stampedAt = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY))
    return !!stampedAt && Date.now() - stampedAt < RELOAD_GUARD_MS
  } catch {
    return false
  }
}

function stampReload(): void {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()))
  } catch {
    // Nothing to do — without the stamp the guard just does not engage.
  }
}

/**
 * Just enough of the nuxtApp to register the hook. Annotated rather than left
 * implicit for the reason given in plugins/05: vue-tsc cannot see Nuxt's
 * auto-import types here (the root tsconfig excludes `.nuxt` and overrides
 * `paths`), so an unannotated param is an implicit-any error. Only the bare
 * `defineNuxtPlugin` below is genuinely unavoidable.
 */
interface IChunkErrorNuxtApp {
  hook: (name: string, handler: (payload: { error?: Error }) => void) => unknown
}

export default defineNuxtPlugin((nuxtApp: IChunkErrorNuxtApp) => {
  // One reload per page life. A single interaction can kick off several lazy
  // imports (a dialog resolves its field spec and its card component together),
  // and each failure fires its own `app:chunkError`.
  let reloadScheduled = false

  nuxtApp.hook('app:chunkError', ({ error }: { error?: Error }) => {
    if (reloadScheduled) {
      return
    }
    reloadScheduled = true

    // eslint-disable-next-line no-console
    console.error(
      `[plugins/06.chunkErrorReload.client.ts] chunk failed to load:`,
      error
    )

    if (reloadedRecently()) {
      // We already reloaded for this and the chunk is still missing, so the
      // deployed build is incomplete. Say so instead of bouncing the page.
      addNotification({
        type: 'error',
        message:
          'Part of the application failed to load.<br>Please reload the page, and tell support if it keeps happening.',
      })
      return
    }

    addNotification({
      type: 'warning',
      message:
        'A new version of Cynque has been released.<br>Reloading to pick it up…',
    })

    setTimeout(() => {
      stampReload()
      // Deliberately not `reloadNuxtApp()`: its `persistState` is inert unless
      // `experimental.restoreState` is on (it is not, and the Pinia plugin
      // already persists what matters), which leaves only its 10s reload guard
      // — replaced above by one long enough to catch a genuinely broken build.
      reloadPage()
    }, RELOAD_DELAY_MS)
  })
})
